import EventBus from '@/helpers/event-bus.js';
import evidenceApi from '../api/evidenceApi';

export default {
  isItemDisabled(item) {
    return this.disabledItems.includes(item.id);
  },

  async fetchEvidences() {
    try {
      const { data: rawEvidences } = await evidenceApi.all();
      this.evidences = rawEvidences;

      // Sort evidence by updated date by default (newest first)
      this.sortEvidence();
    } catch (error) {
      if (error.response.status === 400) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorList.error-404'),
          color: 'grey',
        });
      }
    }
  },

  // Sort evidence items based on current sort field and direction
  sortEvidence() {
    this.evidences.sort((a, b) => {
      if (this.sortField === 'status') {
        if (a.status < b.status) return this.sortDirection === 'asc' ? -1 : 1;
        if (a.status > b.status) return this.sortDirection === 'asc' ? 1 : -1;
        // Secondary sort by updated date if statuses are equal
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      } else {
        const dateA = new Date(a[this.sortField] || 0);
        const dateB = new Date(b[this.sortField] || 0);
        return this.sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
  },

  handleReview() {
    if (this.selectedRadio === 'reject') {
      this.rejectEvidence();
    } else if (this.selectedRadio === 'approve') {
      this.approveEvidence();
    } else if (this.selectedRadio === 'largely') {
      this.largelyCompliantEvidence();
    } else {
      this.reviewEvidence();
    }
  },

  handleCancel() {
    this.reviewClicked = false;
    this.selectedRadio = null;
    this.reviewScore = null;
    this.reviewComments = null;
  },

  enableDeleteDialog(item) {
    this.deleteItem = item;
    this.deleteClicked = true;
  },

  enableCommentsDialog(item) {
    this.commentsClicked = true;
    this.itemWithComments = item;
  },

  enableApproveDialog(item) {
    this.reviewItem = item;
    this.reviewClicked = true;
  },

  async deleteEvidence() {
    this.loading = true;
    try {
      await evidenceApi.delete(this.deleteItem.id);
      const index = this.evidences.findIndex(evidence => evidence.id === this.deleteItem.id);
      if (index !== -1) {
        this.evidences.splice(index, 1);
      }
      EventBus.$emit('snackbar', {
        text: this.$t('delete.success'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error occurred during deletion:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.deleteClicked = false;
      this.loading = false;
    }
  },

  async approveEvidence() {
    this.loading = true;
    try {
      await evidenceApi.approveEvidence(this.reviewItem.id);
      EventBus.$emit('snackbar', {
        text: this.$t('review.success'),
        color: 'green',
      });
      await this.fetchEvidences();
    } catch (error) {
      console.error('Error occurred during approve request:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.reviewClicked = false;
      this.loading = false;
    }
  },

  async rejectEvidence() {
    this.loading = true;
    this.reviewItem = { id: this.reviewItem.id, comments: this.reviewComments };
    try {
      await evidenceApi.rejectEvidence(this.reviewItem);
      EventBus.$emit('snackbar', {
        text: this.$t('review.rejectSuccess'),
        color: 'green',
      });
      await this.fetchEvidences();
    } catch (error) {
      console.error('Error occurred during reject request:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.reviewClicked = false;
      this.loading = false;
      this.reviewComments = null;
    }
  },

  async reviewEvidence() {
    this.loading = true;
    this.reviewItem = {
      id: this.reviewItem.id,
      comments: this.reviewComments,
      complianceScore: this.reviewScore,
    };
    try {
      await evidenceApi.reviewEvidence(this.reviewItem);
      EventBus.$emit('snackbar', {
        text: this.$t('review.reviewSuccess'),
        color: 'green',
      });
      await this.fetchEvidences();
    } catch (error) {
      console.error('Error occurred during review request:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.reviewClicked = false;
      this.loading = false;
      this.reviewComments = null;
      this.reviewScore = null;
    }
  },

  async largelyCompliantEvidence() {
    this.loading = true;
    this.reviewItem = {
      id: this.reviewItem.id,
      comments: this.reviewComments,
      complianceScore: this.reviewScore,
    };
    try {
      await evidenceApi.largelyCompliantEvidence(this.reviewItem);
      EventBus.$emit('snackbar', {
        text: this.$t('review.largelyCompliantSuccess'),
        color: 'green',
      });
      await this.fetchEvidences();
    } catch (error) {
      console.error('Error occurred during largely-compliant request:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.reviewClicked = false;
      this.loading = false;
      this.reviewComments = null;
      this.reviewScore = null;
    }
  },

  byteToUint8Array(byteArray) {
    var uint8Array = new Uint8Array(byteArray.length);
    for (var i = 0; i < uint8Array.length; i++) {
      uint8Array[i] = byteArray[i];
    }
    return uint8Array;
  },

  editEvidence(item) {
    localStorage.setItem('evidence', JSON.stringify(item));
    this.$router.push({ name: 'EvidenceBuilder' });
  },

  async downloadFile() {
    this.loading = true;
    try {
      const response = await evidenceApi.download(this.reviewItem);
      const url = window.URL.createObjectURL(response.data);

      const a = document.createElement('a');
      a.href = url;
      a.download = this.reviewItem.fileName;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      EventBus.$emit('snackbar', {
        text: this.$t('review.successDownload'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error occurred during download request:', error);
      EventBus.$emit('snackbar', {
        text: this.$t('errorlist.something-went-wrong'),
        color: 'red',
      });
    } finally {
      this.loading = false;
    }
  },

  toggleSelection(item) {
    if (!Array.isArray(this.selectedComponents)) {
      console.warn('selectedComponents is not an array:', this.selectedComponents);
      this.selectedComponents = [];
    }

    const index = this.selectedComponents.findIndex(selectedItem => selectedItem.id === item.id);
    let newSelection = [...this.selectedComponents];

    if (index === -1) {
      newSelection.push(item);
    } else {
      newSelection.splice(index, 1);
    }

    this.selectedComponents = newSelection;
    this.$emit('evidence-selected', newSelection);
  },

  statusChipColor(status) {
    switch (status) {
      case 'Draft':
        return 'orange';
      case 'Pending Review':
        return 'primary';
      case 'Reviewed':
        return 'deep-orange';
      case 'Largely Compliant':
        return 'amber darken-2';
      case 'Approved':
        return 'green';
      case 'Rejected':
        return 'red';
      default:
        return 'primary';
    }
  },

  getSourceColor(source) {
    const colorMap = {
      Manual: 'blue',
      manual: 'blue',
      'Threat Spider': 'purple',
      'threat spider': 'purple',
      'Dependency Track': 'orange',
      'dependency track': 'orange',
      'Threat Dragon JSON': 'teal',
      'threat dragon json': 'teal',
    };
    return colorMap[source] || 'blue'; // Default to blue for Manual/undefined
  },

  isThreatSpiderEvidence(evidence) {
    // Identify threat spider evidence based on source field
    // You can expand this to include other automated sources if needed
    return (
      evidence.source === 'Threat Spider' ||
      evidence.source === 'Threat Dragon JSON' ||
      evidence.source === 'Dependency Track'
    );
  },
};
